import { z } from 'zod';
import { CHARGER_AUTH_CHOICES, PLUG_TYPES } from '../enums.js';
import { latitudeField, longitudeField } from './organisations.js';
import { chargeCode } from './payToCharge.js';

const optionalText = (max: number) =>
  z.string().trim().max(max, `Use at most ${max} characters`).nullish().transform((v) => (v ? v : null));

/** Settings an admin or manager can change on a charger. Send only what changes. */
export const chargerUpdateBody = z
  .object({
    label: optionalText(64),
    bay: optionalText(32),
    /** Where it stands, when that's not the site's location (chargers spread around one site). Blank: the site's. */
    latitude: latitudeField,
    longitude: longitudeField,
    siteId: z.string().uuid('Choose a site'),
    authMethod: z.enum(CHARGER_AUTH_CHOICES, { message: 'Choose RFID card or plug and charge' }),
    /** Plug and charge: the driver every session is billed to (e.g. a home charger). */
    assignedDriverId: z.preprocess((v) => (v === '' ? null : v), z.string().uuid('Choose a driver').nullable()),
    /** Public pay to charge: guests scan the QR code and pay by card. Not with plug and charge. */
    publicCharging: z.boolean(),
    /**
     * Turning public charging on: the QR sticker for each socket, from the stickers made ahead
     * (Settings, QR stickers). A blank code makes a new one. Sockets left out get a new one too.
     */
    chargeCodes: z.array(z.object({ connectorId: z.number().int().min(1).max(8), code: chargeCode.nullable() })).max(8),
    /** Pay to charge demo: the QR page runs the whole flow with no card charged and no session started. */
    publicDemo: z.boolean(),
    ratedKw: z.preprocess(
      (v) => (v === '' || v === undefined ? null : v),
      z.coerce.number().min(1, 'At least 1 kW').max(400, 'At most 400 kW').nullable(),
    ),
    /** Plug on each connector, in connector order. */
    plugTypes: z.array(z.enum(PLUG_TYPES, { message: 'Choose Type 2 or CCS2' })).min(1).max(8),
    connectorCount: z.coerce.number().int().min(1, 'At least 1').max(8, 'At most 8'),
    installedOn: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullish()
      .or(z.literal(''))
      .transform((v) => (v ? v : null)),
    notes: optionalText(2000),
    /** Admin only: pin the OCPP handling profile ('auto' = pick from the boot message). */
    vendorProfile: z.string().max(32).nullable(),
    isActive: z.boolean(),
  })
  .partial();
export type ChargerUpdateBody = z.input<typeof chargerUpdateBody>;

/** Accept a charger that connected for the first time, and put it on a site. */
export const acceptChargerBody = z.object({
  siteId: z.string().uuid('Choose a site'),
  label: optionalText(64),
  bay: optionalText(32),
});

/**
 * The OCPP identity: what comes after the server address in the charger's settings, e.g.
 * wss://ocpp.infinityev.com.au/<this>. Printable characters, no spaces or slashes.
 */
export const chargePointId = z
  .string()
  .trim()
  .min(1, 'Enter the charge point ID')
  .max(64, 'At most 64 characters')
  .regex(/^[\x21-\x2e\x30-\x7e]+$/, 'No spaces or slashes: just the ID, not the whole address');

/** Add a charger by hand, before it connects: it's accepted the moment it does. */
export const createChargerBody = acceptChargerBody.extend({
  chargePointId,
  connectorCount: z.coerce.number().int().min(1, 'At least 1').max(8, 'At most 8').default(1),
});
export type CreateChargerBody = z.input<typeof createChargerBody>;

/**
 * Security profile and password (OCPP 1.6 Security Whitepaper). The password is the
 * charger's AuthorizationKey: 16 to 40 characters.
 */
export const chargerSecurityBody = z
  .object({
    securityProfile: z.union([z.literal(0), z.literal(1), z.literal(2)]),
    /** New password; leave out to keep the current one. */
    password: z
      .string()
      .min(16, 'Use at least 16 characters')
      .max(40, 'Use at most 40 characters')
      .regex(/^[\x21-\x7e]+$/, 'Letters, numbers and symbols only, no spaces')
      .optional(),
    /**
     * true: send it to the charger first (ChangeConfiguration AuthorizationKey) and save it
     * only if the charger accepts. false: it's already set on the charger (e.g. by the installer).
     */
    sendToCharger: z.boolean().default(true),
  });

export const CHARGER_COMMANDS = [
  'RemoteStartTransaction',
  'RemoteStopTransaction',
  'Reset',
  'UnlockConnector',
  'TriggerMessage',
  'ChangeAvailability',
  'GetConfiguration',
  'ChangeConfiguration',
  'ClearCache',
] as const;
export type ChargerCommand = (typeof CHARGER_COMMANDS)[number];

const connectorId = z.coerce.number().int().min(1).max(8);

/** A command for a charger, with the fields each one needs. */
export const chargerCommandBody = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('RemoteStartTransaction'),
    connectorId,
    /** Start on this driver's card: the session is theirs, billed as usual. Leave out for an unbilled start. */
    cardId: z.string().uuid().optional(),
    /** Admins: any tag, e.g. for testing a charger. */
    idTag: z.string().trim().min(1).max(20).optional(),
  }),
  z.object({ action: z.literal('RemoteStopTransaction'), transactionId: z.coerce.number().int().positive() }),
  z.object({ action: z.literal('Reset'), type: z.enum(['Soft', 'Hard']) }),
  z.object({ action: z.literal('UnlockConnector'), connectorId }),
  z.object({
    action: z.literal('TriggerMessage'),
    requestedMessage: z.enum(['BootNotification', 'Heartbeat', 'MeterValues', 'StatusNotification', 'DiagnosticsStatusNotification', 'FirmwareStatusNotification']),
    connectorId: connectorId.optional(),
  }),
  z.object({ action: z.literal('ChangeAvailability'), connectorId: z.coerce.number().int().min(0).max(8), type: z.enum(['Operative', 'Inoperative']) }),
  z.object({ action: z.literal('GetConfiguration'), key: z.array(z.string().max(50)).max(50).optional() }),
  z.object({
    action: z.literal('ChangeConfiguration'),
    key: z
      .string()
      .trim()
      .min(1, 'Enter a key')
      .max(50)
      // The password has its own, safer route (Security).
      .refine((k) => k.toLowerCase() !== 'authorizationkey', 'Change the password under Security'),
    value: z.string().max(500),
  }),
  z.object({ action: z.literal('ClearCache') }),
]);
export type ChargerCommandBody = z.infer<typeof chargerCommandBody>;

/** Plain words for each command, for buttons and the history list. */
export const COMMAND_LABELS: Record<ChargerCommand, string> = {
  RemoteStartTransaction: 'Start a session',
  RemoteStopTransaction: 'Stop a session',
  Reset: 'Restart',
  UnlockConnector: 'Unlock the connector',
  TriggerMessage: 'Ask for a message',
  ChangeAvailability: 'Change availability',
  GetConfiguration: 'Read the configuration',
  ChangeConfiguration: 'Change a setting',
  ClearCache: 'Clear the card cache',
};
